import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Form, FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useEmployeesQuery } from "modules/settings/hooks/useEmployeesQuery"
import { useUpdateEmployeeCompanyAccessMutation } from "modules/settings/hooks/useUpdateEmployeeCompanyAccessMutation"
import { useEffect } from "react"
import { useController, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { UpdateEmployeeCompanyAccessSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { EmployeeCompanyAccessSelector } from "../Form/EmployeeCompanyAccessSelector"

type FieldValues = z.infer<typeof UpdateEmployeeCompanyAccessSchema>

type Props = {
  employeeId: string
  onClose: () => void
}

export function EditEmployeeAccess({ employeeId, onClose }: Props) {
  const { t } = useTranslation()
  const { data: currentEmployee } = useAuthEmployeeQuery()
  const { data: employees } = useEmployeesQuery()
  const { mutateAsync, isPending } = useUpdateEmployeeCompanyAccessMutation()

  const employee = employees?.find((item) => item.id === employeeId)
  const companies = currentEmployee?.organization.companies ?? []

  const form = useForm<FieldValues>({
    resolver: zodResolver(UpdateEmployeeCompanyAccessSchema),
    defaultValues: {
      isManager: false,
      companyIds: [],
    },
  })

  const { field: managerField } = useController({
    control: form.control,
    name: "isManager",
  })
  const isManager = managerField.value
  const companyIds = form.watch("companyIds") ?? []
  const isSubmitDisabled = isPending || !employee || (!isManager && companyIds.length === 0)

  useEffect(() => {
    if (!employee) return

    form.reset({
      isManager: employee.isManager,
      companyIds: employee.isManager ? [] : employee.companies.map((company) => company.id),
    })
  }, [employee, form])

  async function onSubmit(data: FieldValues) {
    await mutateAsync({ employeeId, ...data })

    toast.success(t("settings.team.editAccess.success"))
    onClose()
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[475px]">
        <DialogHeader>
          <DialogTitle>{t("settings.team.editAccess.title")}</DialogTitle>
          <DialogDescription>
            {t("settings.team.editAccess.description", {
              name: employee?.name ?? employee?.email ?? "",
            })}
          </DialogDescription>
        </DialogHeader>
        <Form
          {...form}
          errorTranslations={getErrorTranslations()}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="companyIds"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-2">
                <EmployeeCompanyAccessSelector
                  companies={companies}
                  isManager={isManager}
                  companyIds={field.value ?? []}
                  onManagerChange={managerField.onChange}
                  onCompanyIdsChange={field.onChange}
                  label={
                    companies.length > 1
                      ? t("settings.team.inviteEmployee.companyAccess")
                      : t("settings.team.inviteEmployee.role")
                  }
                  description={t("settings.team.editAccess.companyAccessDescription")}
                  disabled={isPending || !employee}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t("shared.cancel")}
            </Button>
            <Button disabled={isSubmitDisabled}>{t("settings.team.editAccess.submit")}</Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
